/**
 * ChatInput component.
 *
 * Text input area with send button. Handles Enter key submission
 * and textarea auto-resize. The onSend callback is the extension
 * point for M01-T02 WebSocket communication.
 */

import { SEND_ICON_SVG } from "./icons";

/** SVG for the attachment/paperclip icon */
const ATTACH_ICON_SVG = `<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m21.44 11.05-9.19 9.19a6 6 0 0 1-8.49-8.49l8.57-8.57A4 4 0 1 1 18 8.84l-8.59 8.57a2 2 0 0 1-2.83-2.83l8.49-8.48"/></svg>`;

export interface ChatInputOptions {
  onSend: (message: string) => void;
  /** Called when the user selects a file for upload */
  onFileSelect?: (file: File) => void;
  placeholder?: string;
  /** Enable the file attachment button (default: false) */
  enableFileUpload?: boolean;
}

export class ChatInput {
  private readonly element: HTMLElement;
  private readonly textareaEl: HTMLTextAreaElement;
  private readonly sendBtn: HTMLButtonElement;
  private readonly fileInput: HTMLInputElement | null = null;
  private readonly attachBtn: HTMLButtonElement | null = null;

  constructor(private readonly options: ChatInputOptions) {
    this.element = document.createElement("div");
    this.element.className = "hana-input";

    // File upload button (optional, before textarea)
    if (options.enableFileUpload) {
      this.fileInput = document.createElement("input");
      this.fileInput.type = "file";
      this.fileInput.accept = "image/jpeg,image/png,image/gif,application/pdf";
      this.fileInput.className = "hana-input__file-input";
      this.fileInput.style.display = "none";
      this.fileInput.setAttribute("aria-label", "Upload file");
      this.fileInput.addEventListener("change", () => {
        const file = this.fileInput?.files?.[0];
        if (file && this.options.onFileSelect) {
          this.options.onFileSelect(file);
        }
        // Reset so the same file can be selected again
        if (this.fileInput) this.fileInput.value = "";
      });
      this.element.appendChild(this.fileInput);

      this.attachBtn = document.createElement("button");
      this.attachBtn.className = "hana-input__attach";
      this.attachBtn.setAttribute("aria-label", "Attach file");
      this.attachBtn.setAttribute("type", "button");
      const attachIcon = document.createElement("span");
      attachIcon.className = "hana-input__attach-icon";
      attachIcon.innerHTML = ATTACH_ICON_SVG;
      this.attachBtn.appendChild(attachIcon);
      this.attachBtn.addEventListener("click", () => {
        this.fileInput?.click();
      });
      this.element.appendChild(this.attachBtn);
    }

    // Textarea
    this.textareaEl = document.createElement("textarea");
    this.textareaEl.className = "hana-input__field";
    this.textareaEl.placeholder = options.placeholder ?? "Type a message...";
    this.textareaEl.setAttribute("aria-label", "Message input");
    this.textareaEl.rows = 1;

    this.textareaEl.addEventListener("keydown", (e: KeyboardEvent) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        this.handleSend();
      }
    });

    this.textareaEl.addEventListener("input", () => {
      this.autoResize();
      this.updateSendState();
    });

    this.element.appendChild(this.textareaEl);

    // Send button
    this.sendBtn = document.createElement("button");
    this.sendBtn.className = "hana-input__send";
    this.sendBtn.setAttribute("aria-label", "Send message");
    this.sendBtn.setAttribute("type", "button");
    this.sendBtn.disabled = true;

    const sendIcon = document.createElement("span");
    sendIcon.className = "hana-input__send-icon";
    sendIcon.innerHTML = SEND_ICON_SVG;
    this.sendBtn.appendChild(sendIcon);

    this.sendBtn.addEventListener("click", () => {
      this.handleSend();
    });

    this.element.appendChild(this.sendBtn);
  }

  getElement(): HTMLElement {
    return this.element;
  }

  /**
   * Focus the textarea. Called when the panel opens.
   */
  focus(): void {
    this.textareaEl.focus();
  }

  /**
   * Get the current input value (trimmed).
   */
  getValue(): string {
    return this.textareaEl.value.trim();
  }

  /**
   * Clear the input and reset height.
   */
  clear(): void {
    this.textareaEl.value = "";
    this.textareaEl.style.height = "auto";
    this.updateSendState();
  }

  /**
   * Enable or disable the input (e.g., while sending).
   */
  setDisabled(disabled: boolean): void {
    this.textareaEl.disabled = disabled;
    this.sendBtn.disabled = disabled;
  }

  /**
   * Update the visible prompt without reconstructing the input.
   */
  setPlaceholder(placeholder: string): void {
    this.textareaEl.placeholder = placeholder;
  }

  private handleSend(): void {
    const value = this.getValue();
    if (value.length === 0) return;

    this.options.onSend(value);
    this.clear();
  }

  private autoResize(): void {
    this.textareaEl.style.height = "auto";
    const maxHeight = 120;
    this.textareaEl.style.height =
      Math.min(this.textareaEl.scrollHeight, maxHeight) + "px";
  }

  private updateSendState(): void {
    this.sendBtn.disabled = this.getValue().length === 0;
  }
}
